import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import GetData from "../../services/getData";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import servicesIcon from "../../media/icons8-catalog-services.png";
import { useParams } from "react-router-dom";
import undefinedService from "../../media/undefined_service.jpg";
import { getLocale } from "../../i18n/config";
import { getGroupTypeLabel } from "../../i18n/groupTypes";
import { useLanguage } from "../../hooks/useLanguage";
import {
  getLocalizedServiceField,
  localizedFieldKey,
} from "../../i18n/localizedService";
import "./products.css";

// value — доменное (русское) значение, сохраняется на сервере; label — переводимый.
const TYPE_VALUES = [
  { value: "Инфузионная терапия", key: "procedure" },
  { value: "Инфузионная терапия (доп.)", key: "procedureExtra" },
  { value: "Пероральные лекарственные препараты", key: "therapy" },
  { value: "Чекапы", key: "diagnostic" },
  { value: "Консультация", key: "consultation" },
  { value: "Пептиды", key: "peptides" },
  { value: "Процедуры", key: "manipulations" },
];

const STATUS_VALUES = [
  { value: "В наличии", key: "inStock" },
  { value: "Нет в наличии", key: "outOfStock" },
  { value: "Ограничено", key: "limited" },
];

/** Ключ статуса по доменному значению. */
const statusKey = (status) => {
  if (status === "active" || status === "В наличии") return "inStock";
  if (status === "limited" || status === "Ограничено") return "limited";
  if (status === "inactive" || status === "Нет в наличии" || status === "Не в наличии") {
    return "outOfStock";
  }
  return null;
};

function ProductsProfile() {
  const { t, i18n } = useTranslation("products");
  const { language } = useLanguage();
  const locale = getLocale(i18n.language);
  // services — теперь ОБЪЕКТ, так как мы загружаем одну запись
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    composition: "",
    price: "",
    technology: "",
  });

  const { id } = useParams();
  const id_services = Number(id);

  // Загрузка данных об услуге
  const fetchServices = async (id_services) => {
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "products",
        block: "info",
        id_services,
      });

      // Обрабатываем как объект, так и массив из одного элемента
      let serviceData = data.result || {};

      if (Array.isArray(serviceData) && serviceData.length > 0) {
        serviceData = serviceData[0];
      }

      if (typeof serviceData !== "object" || serviceData === null) {
        serviceData = {};
      }

      setServices(serviceData);
      console.log(serviceData);
    } catch (err) {
      console.error("Ошибка загрузки услуги:", err);
      setError(err.message || t("profile.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Загружаем услугу при изменении id
  useEffect(() => {
    if (id_services) {
      fetchServices(id_services);
    } else {
      setServices({});
      setLoading(false);
    }
  }, [id_services]);

  // Инициализируем formData, когда данные загружены
  useEffect(() => {
    setFormData({
      name: getLocalizedServiceField(services, "name", language),
      composition: getLocalizedServiceField(services, "composition", language),
      price: services.price?.value?.toString() || "",
      type: services.type?.value || "",
      status: services.status?.value || "",
      technology: getLocalizedServiceField(services, "technology", language),
    });
  }, [services, language]);

  useEffect(() => {
    const localizedName = getLocalizedServiceField(services, "name", language);
    if (localizedName) {
      document.title = localizedName;
    }
  }, [services, language]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const updatedData = {
        [localizedFieldKey("name", language)]: { value: formData.name },
        [localizedFieldKey("composition", language)]: { value: formData.composition },
        price: { value: Number(formData.price) || 0 },
        type: { value: formData.type },
        status: { value: formData.status },
        [localizedFieldKey("technology", language)]: { value: formData.technology },
      };

      // Отправка на сервер
      await GetData({
        module: "products",
        block: "info",
        id_services: id_services,
        action: "change",
        data: updatedData,
      });

      // Обновляем локальное состояние
      setServices((prev) => ({ ...prev, ...updatedData }));
      setIsEditing(false);
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      alert(t("profile.errors.saveFailed"));
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Возвращаем данные к текущему состоянию услуги
    setFormData({
      name: getLocalizedServiceField(services, "name", language),
      composition: getLocalizedServiceField(services, "composition", language),
      price: services.price?.value?.toString() || "",
      type: services.type?.value || "",
      status: services.status?.value || "",
      technology: getLocalizedServiceField(services, "technology", language),
    });
    setIsEditing(false);
  };

  // Защита от рендера до загрузки (опционально, но рекомендуется)
  if (loading) {
    return (
      <Container fluid="md" className="mt-4">
        {t("state.loading", { ns: "common" })}
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid="md" className="mt-4">
        {t("error", { ns: "common", message: error })}
      </Container>
    );
  }

  console.log(formData);

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <div className="patient-page">
            <TitleWithIcon
              icon={servicesIcon}
              title={formData.name || t("untitled", { ns: "common" })}
              showStatus={false}
              status={""}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="tab-content block page-block">
            <Row>
              <Col>
                <BlockTitle
                  title=""
                  buttons={
                    isEditing
                      ? [
                          {
                            label: t("actions.save", { ns: "common" }),
                            onClick: handleSave,
                            type: "normal",
                          },
                          {
                            label: t("actions.cancel", { ns: "common" }),
                            onClick: handleCancel,
                            type: "light",
                          },
                        ]
                      : [
                          {
                            label:(
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                <svg
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                  width="20"
                                  height="20"
                                  style={{ fill: "currentColor" }}>
                                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                                </svg>
                                {t("actions.edit", { ns: "common" })}
                              </span>
                            ),
                            onClick: handleEdit,
                            type: "light icon",
                          },
                        ]
                  }
                />
              </Col>
            </Row>

            <div className="service-container mt-lg-4">
              <Row>
                <Col md={4}>
                  <div className="service-img">
                    <img
                      src={undefinedService}
                      alt={t("profile.imageAlt")}
                      className="img-fluid"
                    />
                  </div>
                </Col>
                <Col md={8}>
                  <div className="service-content">
                    {isEditing ? (
                      <>
                        <div className="field-label">{t("profile.typeLabel")}</div>
                        <select
                          className="form-control mb-2 type-product"
                          value={formData.type}
                          onChange={(e) => handleChange("type", e.target.value)}>
                          {TYPE_VALUES.map((option) => (
                            <option key={option.key} value={option.value}>
                              {t(`types.${option.key}`)}
                            </option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <div className="type-product">
                        {getGroupTypeLabel(t, { name: formData.type })}
                      </div>
                    )}
                    {/* Название */}
                    <div className="title-product-content">
                      {isEditing ? (
                        <>
                          <div className="field-label">{t("profile.nameLabel")}</div>
                          <input
                            type="text"
                            className="form-control mb-2 title-product"
                            value={formData.name}
                            onChange={(e) =>
                              handleChange("name", e.target.value)
                            }
                          />
                          <div className="field-label">{t("profile.statusLabel")}</div>
                          <select
                            className="form-control mb-2 status-select"
                            value={formData.status}
                            onChange={(e) =>
                              handleChange("status", e.target.value)
                            }>
                            {STATUS_VALUES.map((option) => (
                              <option key={option.key} value={option.value}>
                                {t(`statuses.${option.key}`)}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <>
                          <div className="title-product">{formData.name}</div>
                          <span
                            className={`status-badge ${
                              statusKey(formData.status) === "inStock"
                                ? "status-active"
                                : statusKey(formData.status) === "limited"
                                  ? "status-limited"
                                  : "status-inactive"
                            }`}>
                            {statusKey(formData.status)
                              ? t(`statuses.${statusKey(formData.status)}`)
                              : formData.status}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="product-description">
                      {/* Описание */}
                      {isEditing ? (
                        <>
                          <div className="field-label">{t("profile.compositionLabel")}</div>
                          <textarea
                            className="form-control mb-2 service-descript"
                            rows="7"
                            value={formData.composition}
                            onChange={(e) =>
                              handleChange("composition", e.target.value)
                            }
                          />
                        </>
                      ) : (
                        <div className="service-descript">
                          {formData.composition}
                        </div>
                      )}

                      {/* Технология */}
                      <div className="service-technology-wrapper">
                        {isEditing ? (
                          <>
                            <div className="field-label">{t("profile.technologyLabel")}</div>
                            <textarea
                              className="form-control mb-2 service-technology-form"
                              rows="7"
                              value={formData.technology}
                              onChange={(e) =>
                                handleChange("technology", e.target.value)
                              }
                            />
                          </>
                        ) : (
                          <div className="service-technology-form">
                            {formData.technology}
                          </div>
                        )}
                      </div>

                      {/* Цена */}
                      {isEditing ? (
                        <>
                          <div className="field-label">{t("profile.priceLabel")}</div>
                          <input
                            type="number"
                            className="form-control mb-2 product-price"
                            value={formData.price}
                            onChange={(e) =>
                              handleChange("price", e.target.value)
                            }
                            min="0"
                            step="1"
                          />
                        </>
                      ) : (
                        <div className="product-price">
                          {formData.price
                            ? Number(formData.price).toLocaleString(locale) +
                              " " + t("priceSuffix")
                            : t("profile.noPrice")}
                        </div>
                      )}

                      {/* Кнопка "Добавить" — только в режиме просмотра */}
                      {!isEditing && (
                        <div className="product-button">
                          <button
                            className={
                              statusKey(formData.status) === "inStock" ||
                              statusKey(formData.status) === "limited"
                                ? "in-stock"
                                : "lack"
                            }>
                            {statusKey(formData.status) === "inStock" ||
                            statusKey(formData.status) === "limited"
                              ? t("profile.add")
                              : t("profile.notify")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default ProductsProfile;
